import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { RotateCcw } from 'lucide-react';
import { toast } from 'react-toastify';

import { PAGES } from '../../../constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { DetailFormCheckbox, DetailFormValue } from 'components/DetailsView';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { unstable_usePrompt } from 'react-router-dom';

import {
    DynamicDeliveryContractType,
    MicrostoreColumnTemplateType,
    OpsParametersQuery,
    ProductFlow,
    useOpsParametersQuery,
    useUpdateOpsParameterMutation,
} from 'data/__generated__';

// One tab of the thresholds grid per rule: the site contract for dynamic columns, « Livraison fixe » for the
// fixed-flow columns (kept very low by Ops so a fixed site only ships dynamically in an emergency).
const CONTRACT_TYPE_TABS: { contractType: DynamicDeliveryContractType; label: string }[] = [
    { contractType: DynamicDeliveryContractType.Subscription, label: 'Abonnement' },
    { contractType: DynamicDeliveryContractType.FreeDeposit, label: 'Dépôt gratuit' },
    { contractType: DynamicDeliveryContractType.FixedDelivery, label: 'Livraison fixe' },
];

type DynamicDeliveryThresholdEntryFormData = {
    templateType: MicrostoreColumnTemplateType;
    contractType: DynamicDeliveryContractType;
    qualityPassPercent: number;
    criticalPassPercent: number;
    requiredPassPercent: number;
    criticalGroupingPercent: number;
    requiredGroupingPercent: number;
    groupingRadiusKm: number;
    maxDaysWithoutDelivery: number;
};

type DynamicDeliveryTemplateCapacityFormData = {
    templateType: MicrostoreColumnTemplateType;
    estimatedTransactionCapacity: number;
};

type FormData = {
    shouldAutoOrderMenuSupplyOrder: boolean;
    shouldProcessFreshReassortment: boolean;
    canUseKraftForStore: boolean;
    canUseKraftForFreefood: boolean;
    emptyWeightPaletteBox: number;
    emptyWeightKraftBox: number;
    externalVolumePaletteBox: number;
    externalVolumeKraftBox: number;
    volumePaletteBox: number;
    volumeKraftBox: number;
    weightPaletteBox: number;
    weightKraftBox: number;
    serviceTimePerBox: number;
    dryReassortMinProductCount: number;
    dryReassortMinReferenceCount: number;
    dryReassortMaxEmptyFacings: number;
    flowParameters: Array<{
        flow: ProductFlow;
        emptyWeightBox: number;
        externalVolumeBox: number;
        volumeBox: number;
        weightBox: number;
        bundleCapacity: number;
    }>;
    dynamicDelivery: {
        capacity: number;
        thresholds: DynamicDeliveryThresholdEntryFormData[];
        estimatedTransactionCapacities: DynamicDeliveryTemplateCapacityFormData[];
    };
};

type OpsParameterFormProps = {
    opsParameter: OpsParametersQuery['opsParameters'];
};

export const OpsParameterForm = ({ opsParameter }: OpsParameterFormProps) => {
    const [updateOpsParameter, { loading: updateLoading }] = useUpdateOpsParameterMutation();
    const { refetch } = useOpsParametersQuery();
    const [selectedContractType, setSelectedContractType] = useState<DynamicDeliveryContractType>(
        DynamicDeliveryContractType.Subscription,
    );

    const methods = useForm<FormData>({
        defaultValues: opsParameter.parameters,
    });

    const {
        formState: { isDirty },
        handleSubmit,
        register,
        reset,
        watch,
    } = methods;

    const handleFormSubmit = handleSubmit(async (data) => {
        try {
            await updateOpsParameter({
                variables: {
                    parameters: data,
                },
            });

            toast.success('Les paramètres ont bien été modifiés');
            reset(data);
            await refetch();
        } catch (error) {
            toast.error("Une erreur s'est produite lors de la modification des paramètres");
        }
    });

    unstable_usePrompt({
        message: 'Êtes-vous sûr.e de vouloir quitter cette page sans enregistrer ?',
        when: ({ currentLocation, nextLocation }) => isDirty && currentLocation.pathname !== nextLocation.pathname,
    });

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Formulaire',
            items: [
                {
                    label: 'Réinitialiser',
                    icon: <RotateCcw />,
                    onClick: () => reset(),
                    disabled: !isDirty || updateLoading,
                },
            ],
        },
    ];

    return (
        <FormProvider {...methods}>
            <Container>
                <Header>
                    <HeaderTitle>
                        <PageTitle page={PAGES.opsParameters} />
                    </HeaderTitle>
                    <ActionsAndSave
                        actionGroups={actionGroups}
                        onSave={handleFormSubmit}
                        saveDisabled={!isDirty}
                        saveLoading={updateLoading}
                    />
                </Header>
                <Content>
                    <Form>
                        <Section>
                            <SectionTitle>Paramètres booléens</SectionTitle>
                            <FieldRow>
                                <FieldLabel>Auto-commander les commandes fournisseur menu</FieldLabel>
                                <DetailFormCheckbox name="shouldAutoOrderMenuSupplyOrder" />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Traiter le réassortiment fresh</FieldLabel>
                                <DetailFormCheckbox name="shouldProcessFreshReassortment" />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Peut utiliser kraft pour store</FieldLabel>
                                <DetailFormCheckbox name="canUseKraftForStore" />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Peut utiliser kraft pour freefood</FieldLabel>
                                <DetailFormCheckbox name="canUseKraftForFreefood" />
                            </FieldRow>
                        </Section>

                        <Section>
                            <SectionTitle>Paramètres numériques globaux</SectionTitle>
                            <FieldRow>
                                <FieldLabel>Poids vide palette (g)</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="0.01"
                                    {...register('emptyWeightPaletteBox', { required: true, valueAsNumber: true })}
                                />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Poids vide kraft (g)</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="0.01"
                                    {...register('emptyWeightKraftBox', { required: true, valueAsNumber: true })}
                                />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Volume externe palette (cm³)</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="0.01"
                                    {...register('externalVolumePaletteBox', { required: true, valueAsNumber: true })}
                                />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Volume externe kraft (cm³)</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="0.01"
                                    {...register('externalVolumeKraftBox', { required: true, valueAsNumber: true })}
                                />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Volume palette (cm³)</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="0.01"
                                    {...register('volumePaletteBox', { required: true, valueAsNumber: true })}
                                />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Volume kraft (cm³)</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="0.01"
                                    {...register('volumeKraftBox', { required: true, valueAsNumber: true })}
                                />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Poids palette (g)</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="0.01"
                                    {...register('weightPaletteBox', { required: true, valueAsNumber: true })}
                                />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Poids kraft (g)</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="0.01"
                                    {...register('weightKraftBox', { required: true, valueAsNumber: true })}
                                />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Temps de service par caisse</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="0.00001"
                                    {...register('serviceTimePerBox', { required: true, valueAsNumber: true })}
                                />
                            </FieldRow>
                        </Section>

                        <Section>
                            <SectionTitle>Seuils du réassort dry</SectionTitle>
                            <SectionDescription>
                                Le S.T. est généré (livré) dès qu’<strong>au moins un</strong> de ces seuils est
                                atteint, c’est-à-dire quand la valeur de la commande est{' '}
                                <strong>supérieure ou égale</strong> au seuil. Il n’est annulé (box trop peu remplie)
                                que si les <strong>trois</strong> seuils sont en-dessous.
                            </SectionDescription>
                            <FieldRow>
                                <FieldLabel>Quantité totale minimum pour livrer</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="1"
                                    {...register('dryReassortMinProductCount', {
                                        required: true,
                                        valueAsNumber: true,
                                    })}
                                />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Nombre minimum de références pour livrer</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="1"
                                    {...register('dryReassortMinReferenceCount', {
                                        required: true,
                                        valueAsNumber: true,
                                    })}
                                />
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Nombre minimum de facings vides pour livrer</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="1"
                                    {...register('dryReassortMaxEmptyFacings', {
                                        required: true,
                                        valueAsNumber: true,
                                    })}
                                />
                            </FieldRow>
                        </Section>

                        <Section>
                            <SectionTitle>Paramètres par flow</SectionTitle>
                            <FlowTable>
                                <thead>
                                    <FlowTableRow>
                                        <FlowTableHeaderCell>Paramètre</FlowTableHeaderCell>
                                        {watch('flowParameters').map((flowParam) => (
                                            <FlowTableHeaderCell key={flowParam.flow}>
                                                {flowParam.flow.charAt(0).toUpperCase() + flowParam.flow.slice(1)}
                                            </FlowTableHeaderCell>
                                        ))}
                                    </FlowTableRow>
                                </thead>
                                <tbody>
                                    <FlowTableRow>
                                        <FlowTableLabelCell>Poids vide (g)</FlowTableLabelCell>
                                        {watch('flowParameters').map((flowParam, index) => (
                                            <FlowTableDataCell key={`${flowParam.flow}-emptyWeight`}>
                                                <DetailFormValue
                                                    type="number"
                                                    step="0.01"
                                                    {...register(`flowParameters.${index}.emptyWeightBox`, {
                                                        required: true,
                                                        valueAsNumber: true,
                                                    })}
                                                />
                                            </FlowTableDataCell>
                                        ))}
                                    </FlowTableRow>
                                    <FlowTableRow>
                                        <FlowTableLabelCell>Volume externe (cm³)</FlowTableLabelCell>
                                        {watch('flowParameters').map((flowParam, index) => (
                                            <FlowTableDataCell key={`${flowParam.flow}-externalVolume`}>
                                                <DetailFormValue
                                                    type="number"
                                                    step="0.01"
                                                    {...register(`flowParameters.${index}.externalVolumeBox`, {
                                                        required: true,
                                                        valueAsNumber: true,
                                                    })}
                                                />
                                            </FlowTableDataCell>
                                        ))}
                                    </FlowTableRow>
                                    <FlowTableRow>
                                        <FlowTableLabelCell>Volume (cm³)</FlowTableLabelCell>
                                        {watch('flowParameters').map((flowParam, index) => (
                                            <FlowTableDataCell key={`${flowParam.flow}-volume`}>
                                                <DetailFormValue
                                                    type="number"
                                                    step="0.01"
                                                    {...register(`flowParameters.${index}.volumeBox`, {
                                                        required: true,
                                                        valueAsNumber: true,
                                                    })}
                                                />
                                            </FlowTableDataCell>
                                        ))}
                                    </FlowTableRow>
                                    <FlowTableRow>
                                        <FlowTableLabelCell>Poids (g)</FlowTableLabelCell>
                                        {watch('flowParameters').map((flowParam, index) => (
                                            <FlowTableDataCell key={`${flowParam.flow}-weight`}>
                                                <DetailFormValue
                                                    type="number"
                                                    step="0.01"
                                                    {...register(`flowParameters.${index}.weightBox`, {
                                                        required: true,
                                                        valueAsNumber: true,
                                                    })}
                                                />
                                            </FlowTableDataCell>
                                        ))}
                                    </FlowTableRow>
                                    <FlowTableRow>
                                        <FlowTableLabelCell>Capacité bundle</FlowTableLabelCell>
                                        {watch('flowParameters').map((flowParam, index) => (
                                            <FlowTableDataCell key={`${flowParam.flow}-bundleCapacity`}>
                                                <DetailFormValue
                                                    type="number"
                                                    step="1"
                                                    {...register(`flowParameters.${index}.bundleCapacity`, {
                                                        required: true,
                                                        valueAsNumber: true,
                                                    })}
                                                />
                                            </FlowTableDataCell>
                                        ))}
                                    </FlowTableRow>
                                </tbody>
                            </FlowTable>
                        </Section>

                        <Section>
                            <SectionTitle>Livraison dynamique</SectionTitle>
                            <FieldRow>
                                <FieldLabel>Capacité globale (nb colonnes par jour)</FieldLabel>
                                <DetailFormValue
                                    type="number"
                                    step="1"
                                    min="0"
                                    {...register('dynamicDelivery.capacity', {
                                        required: true,
                                        valueAsNumber: true,
                                        min: 0,
                                    })}
                                />
                            </FieldRow>
                            <ContractTypeSwitch>
                                {CONTRACT_TYPE_TABS.map(({ contractType, label }) => (
                                    <ContractTypeButton
                                        key={contractType}
                                        $active={selectedContractType === contractType}
                                        onClick={() => setSelectedContractType(contractType)}
                                        type="button"
                                    >
                                        {label}
                                    </ContractTypeButton>
                                ))}
                            </ContractTypeSwitch>
                            <FlowTable>
                                <thead>
                                    <FlowTableRow>
                                        <FlowTableHeaderCell>Template</FlowTableHeaderCell>
                                        <FlowTableHeaderCell>Qualité % (slots vides max)</FlowTableHeaderCell>
                                        <FlowTableHeaderCell>Passage critique %</FlowTableHeaderCell>
                                        <FlowTableHeaderCell>Passage requis %</FlowTableHeaderCell>
                                        <FlowTableHeaderCell>Regroupement critique %</FlowTableHeaderCell>
                                        <FlowTableHeaderCell>Regroupement requis %</FlowTableHeaderCell>
                                        <FlowTableHeaderCell>Rayon regroupement (km)</FlowTableHeaderCell>
                                        <FlowTableHeaderCell>Jours sans livraison max</FlowTableHeaderCell>
                                    </FlowTableRow>
                                </thead>
                                <tbody>
                                    {watch('dynamicDelivery.thresholds').map((entry, index) => {
                                        if (entry.contractType !== selectedContractType) {
                                            return null;
                                        }
                                        return (
                                            <FlowTableRow key={`${entry.templateType}-${entry.contractType}`}>
                                                <FlowTableLabelCell>{entry.templateType}</FlowTableLabelCell>
                                                <FlowTableDataCell>
                                                    <DetailFormValue
                                                        type="number"
                                                        step="1"
                                                        min="0"
                                                        max="100"
                                                        {...register(
                                                            `dynamicDelivery.thresholds.${index}.qualityPassPercent`,
                                                            {
                                                                required: true,
                                                                valueAsNumber: true,
                                                                min: 0,
                                                                max: 100,
                                                            },
                                                        )}
                                                    />
                                                </FlowTableDataCell>
                                                <FlowTableDataCell>
                                                    <DetailFormValue
                                                        type="number"
                                                        step="1"
                                                        min="0"
                                                        max="100"
                                                        {...register(
                                                            `dynamicDelivery.thresholds.${index}.criticalPassPercent`,
                                                            {
                                                                required: true,
                                                                valueAsNumber: true,
                                                                min: 0,
                                                                max: 100,
                                                            },
                                                        )}
                                                    />
                                                </FlowTableDataCell>
                                                <FlowTableDataCell>
                                                    <DetailFormValue
                                                        type="number"
                                                        step="1"
                                                        min="0"
                                                        max="100"
                                                        {...register(
                                                            `dynamicDelivery.thresholds.${index}.requiredPassPercent`,
                                                            {
                                                                required: true,
                                                                valueAsNumber: true,
                                                                min: 0,
                                                                max: 100,
                                                            },
                                                        )}
                                                    />
                                                </FlowTableDataCell>
                                                <FlowTableDataCell>
                                                    <DetailFormValue
                                                        type="number"
                                                        step="1"
                                                        min="0"
                                                        max="100"
                                                        {...register(
                                                            `dynamicDelivery.thresholds.${index}.criticalGroupingPercent`,
                                                            {
                                                                required: true,
                                                                valueAsNumber: true,
                                                                min: 0,
                                                                max: 100,
                                                            },
                                                        )}
                                                    />
                                                </FlowTableDataCell>
                                                <FlowTableDataCell>
                                                    <DetailFormValue
                                                        type="number"
                                                        step="1"
                                                        min="0"
                                                        max="100"
                                                        {...register(
                                                            `dynamicDelivery.thresholds.${index}.requiredGroupingPercent`,
                                                            {
                                                                required: true,
                                                                valueAsNumber: true,
                                                                min: 0,
                                                                max: 100,
                                                            },
                                                        )}
                                                    />
                                                </FlowTableDataCell>
                                                <FlowTableDataCell>
                                                    <DetailFormValue
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        {...register(
                                                            `dynamicDelivery.thresholds.${index}.groupingRadiusKm`,
                                                            {
                                                                required: true,
                                                                valueAsNumber: true,
                                                                min: 0,
                                                            },
                                                        )}
                                                    />
                                                </FlowTableDataCell>
                                                <FlowTableDataCell>
                                                    <DetailFormValue
                                                        type="number"
                                                        step="1"
                                                        min="0"
                                                        {...register(
                                                            `dynamicDelivery.thresholds.${index}.maxDaysWithoutDelivery`,
                                                            {
                                                                required: true,
                                                                valueAsNumber: true,
                                                                min: 0,
                                                            },
                                                        )}
                                                    />
                                                </FlowTableDataCell>
                                            </FlowTableRow>
                                        );
                                    })}
                                </tbody>
                            </FlowTable>
                        </Section>

                        <Section>
                            <SectionTitle>Capacité transactionnelle estimée par type de template</SectionTitle>
                            {watch('dynamicDelivery.estimatedTransactionCapacities').map((entry, index) => (
                                <FieldRow key={entry.templateType}>
                                    <FieldLabel>{entry.templateType}</FieldLabel>
                                    <DetailFormValue
                                        type="number"
                                        step="1"
                                        min="0"
                                        {...register(
                                            `dynamicDelivery.estimatedTransactionCapacities.${index}.estimatedTransactionCapacity`,
                                            {
                                                required: true,
                                                valueAsNumber: true,
                                                min: 0,
                                            },
                                        )}
                                    />
                                </FieldRow>
                            ))}
                        </Section>
                    </Form>
                </Content>
            </Container>
        </FormProvider>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    padding: 15px;
    flex: 1;
    overflow: scroll;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const Section = styled.div`
    margin-bottom: 30px;
    padding: 20px;
    background-color: ${({ theme }) => theme.backgroundColor};
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const SectionTitle = styled.h3`
    margin: 0 0 20px 0;
    font-size: 18px;
    font-weight: bold;
    color: ${({ theme }) => theme.textColor};
`;

const SectionDescription = styled.p`
    margin: -10px 0 20px 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${({ theme }) => theme.infoTextColor};
`;

const FlowTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
`;

const FlowTableHeaderCell = styled.th`
    padding: 12px;
    text-align: left;
    font-weight: bold;
    border-bottom: 2px solid ${({ theme }) => theme.lightBorderColor};
    color: ${({ theme }) => theme.textColor};
    text-transform: capitalize;

    &:first-child {
        min-width: 200px;
    }
`;

const FlowTableRow = styled.tr``;

const FlowTableLabelCell = styled.td`
    padding: 12px;
    font-weight: 500;
    border-right: 1px solid ${({ theme }) => theme.lightBorderColor};
    color: ${({ theme }) => theme.textColor};
`;

const FlowTableDataCell = styled.td`
    padding: 8px 12px;
    text-align: center;
`;

const FieldRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    gap: 15px;
`;

const FieldLabel = styled.label`
    min-width: 250px;
    font-weight: 500;
    color: ${({ theme }) => theme.textColor};
`;

const ContractTypeSwitch = styled.div`
    display: flex;
    gap: 0;
    margin-bottom: 16px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    width: fit-content;
`;

const ContractTypeButton = styled.button<{ $active: boolean }>`
    padding: 8px 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    background-color: ${({ $active, theme }) => ($active ? theme.ctaPrimaryColor : 'transparent')};
    color: ${({ $active, theme }) => ($active ? '#fff' : theme.textColor)};
    transition:
        background-color 0.2s,
        color 0.2s;
`;
